import { erpForm, erpForms, erpRouteTable } from './store';
import type { ErpRoute, ErpRouteEntry } from './types';

/**
 * Readers of the served route table (BLP/nx#3257): which route opens a
 * resource's list, record and create screen. Names and paths are the table's;
 * the components behind them, the breadcrumbs, props and permissions stay in the
 * routers. Like every definitions accessor they throw before the definitions are
 * loaded, so they belong to modules of the ERP bundle (routers, views) and to
 * functions — not to module-level code of the shell's eager slice.
 */

/** The table's entry of one resource, or undefined when it has none. */
export function erpRouteEntry(key: string): ErpRouteEntry | undefined {
  return Object.hasOwn(erpRouteTable().resources, key) ? erpRouteTable().resources[key] : undefined;
}

function requireRoute(key: string, kind: 'list' | 'record' | 'create'): ErpRoute {
  const route = erpRouteEntry(key)?.[kind];
  if (!route) throw new Error(`ERP route table has no ${kind} route for "${key}"`);
  return route;
}

/** The list route (name + path) of a resource. */
export const erpListRoute = (key: string): ErpRoute => requireRoute(key, 'list');

/** The create route (name + path) of a resource. */
export const erpCreateRoute = (key: string): ErpRoute => requireRoute(key, 'create');

/** The record route of a resource; `param` names the route parameter that carries the record id. */
export function erpRecordRoute(key: string): ErpRoute & { param: string } {
  const route = requireRoute(key, 'record');
  if (!route.param) throw new Error(`ERP record route of "${key}" names no id parameter`);
  return { ...route, param: route.param };
}

/**
 * The REST route of a resource (`/customers`): its form's binding, else the
 * entry's own `basePath` (the tasks, the projects and the billing runs have no
 * form). Undefined when neither names one.
 */
export function findErpResourceBasePath(key: string): string | undefined {
  const entry = erpRouteEntry(key);
  const formKey = entry?.form ?? key;
  const form = Object.hasOwn(erpForms(), formKey) ? erpForm(formKey) : undefined;
  return form?.resource?.basePath ?? entry?.basePath;
}

/** `findErpResourceBasePath`, throwing when the definitions name no REST route. */
export function erpResourceBasePath(key: string): string {
  const basePath = findErpResourceBasePath(key);
  if (!basePath) throw new Error(`ERP definitions name no REST route for "${key}"`);
  return basePath;
}

/** A path parameter of the table: `:id`, `:id(\\d+)`, optionally `?`. */
const PATH_PARAM = /:(\w+)(?:\(((?:[^()\\]|\\.)*)\))?\??/g;

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The path of a resource's record route with its parameters filled in:
 * `erpRecordPath('customers', 5)` → `/masterdata/customers/5`. `id` fills the
 * route's own `param`; a nested route (a contract below its partner) takes the
 * other parameters in `parents`. Relative to the ERP's base path.
 */
export function erpRecordPath(
  key: string,
  id: string | number,
  parents: Readonly<Record<string, string | number>> = {}
): string {
  const { path, param } = erpRecordRoute(key);
  const values: Record<string, string | number> = { ...parents, [param]: id };
  return path.replace(PATH_PARAM, (_whole, name: string) => {
    if (!Object.hasOwn(values, name)) {
      throw new Error(`ERP record route of "${key}" needs the parameter "${name}"`);
    }
    return String(values[name]);
  });
}

/**
 * A matcher for the record route's paths: the parameters are capture groups in
 * the order of the path (`:partnerId` first, `:contractId` second), each with
 * the pattern the route declares. `prefix` is put in front of the path (the
 * shell's `/erp`).
 */
export function erpRecordPathMatcher(key: string, prefix = ''): RegExp {
  const { path } = erpRecordRoute(key);
  let source = '';
  let last = 0;
  for (const match of path.matchAll(PATH_PARAM)) {
    source += escapeRegExp(path.slice(last, match.index));
    source += `(${match[2] ?? '[^/]+'})`;
    last = match.index + match[0].length;
  }
  source += escapeRegExp(path.slice(last));
  return new RegExp(`^${escapeRegExp(prefix)}${source}$`);
}
